import React, { useState, useEffect } from 'react';
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import CategoryPage from './pages/CategoryPage';
import PackageDetailPage from './pages/PackageDetailPage';
import PassengerDetailsPage from './pages/PassengerDetailsPage';
import AddOnsPage from './pages/AddOnsPage';
import CheckoutPage from './pages/CheckoutPage';
import CustomerPortal from './pages/CustomerPortal';
import ShoppingCart from './pages/ShoppingCart';
import ShopPage from './pages/ShopPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import FlightSchedulePage from './pages/FlightSchedulePage';
import AIChatBot from './components/AIChatBot';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import { CartProvider } from './context/CartContext';
import { getStoredUser, clearStoredUser } from './utils/safeStorage';

function App() {
  const [currentPage, setCurrentPage] = useState('landing');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBlogSlug, setSelectedBlogSlug] = useState(null);
  const [user, setUser] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [bookingData, setBookingData] = useState({
    rooms: [],
    passengers: [],
    addons: [],
    contactInfo: {},
    emergencyContact: {}
  });

  useEffect(() => {
    setUser(getStoredUser());
    // On page load — check if a ?package=slug param is in the URL
    const params = new URLSearchParams(window.location.search);
    const pkgSlug = params.get('package');
    if (pkgSlug) {
      setSelectedPackage({ slug: pkgSlug });
      setCurrentPage('package');
    }
  }, []);

  const navigate = (page, data = {}) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (data.package) {
      setSelectedPackage(data.package);
      // Update URL so the package link is shareable
      const url = new URL(window.location.href);
      url.searchParams.set('package', data.package.slug);
      window.history.pushState({}, '', url.toString());
    } else {
      // Clear package param when navigating away
      const url = new URL(window.location.href);
      url.searchParams.delete('package');
      window.history.pushState({}, '', url.toString());
    }
    if (data.category) setSelectedCategory(data.category);
    if (data.slug) setSelectedBlogSlug(data.slug);
    if (data.bookingData) setBookingData(prev => ({ ...prev, ...data.bookingData }));
    if (data.orderData) setOrderData(data.orderData);
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleAdminLogin = (adminData) => {
    console.log('Admin logged in:', adminData);
  };

  const handleLogout = () => {
    clearStoredUser();
    setUser(null);
    navigate('landing');
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'landing':
        return <LandingPage navigate={navigate} user={user} onLogout={handleLogout} />;
      case 'about':
        return <AboutPage navigate={navigate} />;
      case 'contact':
        return <ContactPage navigate={navigate} />;
      case 'category':
        return <CategoryPage navigate={navigate} category={selectedCategory} />;
      case 'package':
        return <PackageDetailPage navigate={navigate} packageData={selectedPackage} bookingData={bookingData} />;
      case 'passengers':
        return <PassengerDetailsPage navigate={navigate} packageData={selectedPackage} bookingData={bookingData} />;
      case 'addons':
        return <AddOnsPage navigate={navigate} packageData={selectedPackage} bookingData={bookingData} />;
      case 'checkout':
        return <CheckoutPage navigate={navigate} packageData={selectedPackage} bookingData={bookingData} />;
      case 'portal':
        return <CustomerPortal navigate={navigate} user={user} onLogout={handleLogout} />;
      case 'cart':
        return <ShoppingCart navigate={navigate} />;
      case 'shop':
        return <ShopPage navigate={navigate} />;
      case 'login':
        return <LoginPage navigate={navigate} onLogin={handleLogin} />;
      case 'register':
        return <RegisterPage navigate={navigate} />;
      case 'forgot-password':
        return <ForgotPasswordPage navigate={navigate} />;
      case 'success':
        return <OrderSuccessPage navigate={navigate} orderData={orderData} />;
      case 'admin-login':
        return <AdminLogin navigate={navigate} onAdminLogin={handleAdminLogin} />;
      case 'admin-dashboard':
        return <AdminDashboard navigate={navigate} />;
      case 'blog':
        return <BlogPage navigate={navigate} />;
      case 'blog-detail':
        return <BlogDetailPage navigate={navigate} slug={selectedBlogSlug} />;
      case 'flights':
        return <FlightSchedulePage navigate={navigate} />;
      default:
        return <LandingPage navigate={navigate} user={user} onLogout={handleLogout} />;
    }
  };

  const hideChat = currentPage === 'admin-login' || currentPage === 'admin-dashboard';

  return (
    <CartProvider>
      <ToastProvider>
        <ErrorBoundary>
          <div className="min-h-screen bg-cream overflow-x-hidden">
            {renderPage()}
            <AIChatBot hidden={hideChat} />
            {!hideChat && (
              <a
                href="https://wa.me/6598201134"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat on WhatsApp"
                className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] left-3 sm:bottom-6 sm:left-6 z-40 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#25D366] text-white shadow-xl flex items-center justify-center hover:scale-110 transition-transform"
              >
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.5 3.5A11 11 0 006.3 18.7L3 21l2.4-3.2A11 11 0 1020.5 3.5zm-8.5 16a9 9 0 01-4.6-1.3l-.3-.2-2.7.9.9-2.6-.2-.3A9 9 0 1112 19.5zm5-6.7c-.3-.1-1.6-.8-1.9-.9s-.4-.1-.6.1-.7.9-.8 1-.3.2-.6.1a7.4 7.4 0 01-2.2-1.4 8.2 8.2 0 01-1.5-1.9c-.2-.3 0-.4.1-.6l.4-.5.1-.3a.5.5 0 000-.5c0-.1-.6-1.4-.8-1.9s-.4-.4-.6-.4h-.5a1 1 0 00-.7.3 2.9 2.9 0 00-.9 2.2 5 5 0 001.1 2.6 11.5 11.5 0 004.4 3.9c1.6.7 2.2.8 3 .7a2.5 2.5 0 001.7-1.2 2 2 0 00.1-1.2c-.1-.2-.3-.2-.6-.3z" />
                </svg>
              </a>
            )}
          </div>
        </ErrorBoundary>
      </ToastProvider>
    </CartProvider>
  );
}

export default App;
